// =============================
// File: src/views/Layouts.jsx
// Notes (TH):
// - ลดการเข้าถึง DOM ตรง ๆ ซ้ำ ๆ, รวมเป็นฟังก์ชัน setCssVars / updateManifest
// - ป้องกัน error ตอน SSR/ไม่มี element ด้วย optional checks
// - ปรับ dependency ของ useEffect ให้ถูกต้องและไม่ยิงซ้ำ
// - กัน GTM initialize ซ้ำด้วย useRef
// - ลดโค้ดซ้ำ, ทำ Safe JSON parse กลาง
// - จัดการ query params ที่เป็น boolean อย่างถูกต้อง
// - Cleanup URL.createObjectURL ด้วย URL.revokeObjectURL ใน useEffect cleanup
// - รักษา behavior เดิมทุกจุด (login/register/remember/affiliate/ref/...)
// =============================

import { useEffect, useMemo, useRef, useState } from 'react'
import TagManager from 'react-gtm-module'

// Auth
import { isToken, isUserLoggedIn } from '@core/auth/utils'

// Hooks
import useEffectOnce from '@utility/hooks/useEffectOnce'
import useParameterUrl from '@utility/hooks/useParameterUrl'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

// Routes
import { BrowserRouter as Router } from 'react-router-dom'
import ViewsRoutes from './ViewsRoutes'

// @Utility
import { setStorage, getStorage, removeStorage } from '@utility/app/storage'
import loadLiveChat from '@utility/app/loadLiveChat'

// Redux & Action
import { useDispatch, useSelector } from 'react-redux'
import { setDataCMS } from '@core/redux/actions/app/cms'
import { loadSetting as loadSettingAction } from '@core/redux/actions/app/dataSetting' // ใช้เวอร์ชันที่ปรับใหม่ด้านล่าง
import { loadDataCampAll } from '@core/redux/actions/camp'
import { openAuthenApp, loginToken } from '@core/redux/actions/auth'

// Component
import LoadSpinner from 'views/components/LoadSpinner'
import ErrorApp from 'views/components/Error'
import ConfigSet from 'views/components/ConfigSet'
import NotificationSubscription from 'views/components/NotificationSubscription'

const STORAGE_KEYS = {
    setting: 'setting',
    dataCMS: 'data_cms',
    agentContact: 'agent_contact',
    agentLogo: 'agent_logo',
    allusion: 'allusion',
    affiliate: 'affiliate',
    clickid: 'clickid',
    platform: 'platform',
    fbclid: 'fbclid',
    fbctimestamp: 'fbctimestamp',
    refer: 'refer',
    market: 'market',
    notificationStatus: 'notificationStatus'
}

const truthy = (v) => v === true || v === 'true'
const safeJSONParse = (txt) => {
    try { return JSON.parse(txt) } catch { return null }
}

const setCssVars = (vars = {}) => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const mapping = {
        '--bg-navbar': 'bg-navbar',
        '--bg-primary': 'bg-primary',
        '--bg-secondary': 'bg-secondary',
        '--bt-confirm': 'bt-confirm',
        '--bg-confirm': 'bg-confirm',
        '--text-bt-confirm': 'text-bt-confirm',
        '--bt-login': 'bt-login',
        '--text-bt-login': 'text-bt-login',
        '--bt-register': 'bt-register',
        '--text-bt-register': 'text-bt-register',
        '--bg-brand': 'bg-brand',
        '--bg-label-money': 'bg-label-money',
        '--bg-camp': 'bg-camp',
        '--text-secondary': 'text-secondary',
        '--icon-bg-card': 'icon-bg-card',
        '--bg-card': 'bg-card',
        '--bg-select': 'bg-select',
        '--bg-floor': 'bg-floor',
        '--bg-menu-floor': 'bg-menu-floor',
        '--bg-label-text': 'bg-label-text',
        '--bt-sucess': 'bt-sucess',
        '--bt-danger': 'bt-danger',
        '--bg-warning': 'bg-warning',
        '--bg-blue': 'bg-blue',
        '--bg-orange': 'bg-orange',
        '--bg-black': 'bg-black',
        '--bg-white': 'bg-white',
        '--bg-gray': 'bg-gray',
        '--bg-text-main': 'bg-text-main',
        '--bg-play-game': 'bg-play-game',
        '--bg-popup': 'bg-popup',
        '--bg-floor-popup': 'bg-floor-popup',
        '--label-hot': 'label-hot',
        '--label-new': 'label-new',
        '--label-free-spin': 'label-free-spin',
        '--label-maintenance': 'label-maintenance',
        '--bg-tertiary': 'bg-tertiary',
        '--bg-line': 'bg-line',
        '--tag-high': 'tag-high',
        '--tag-medium': 'tag-medium',
        '--tag-low': 'tag-low',
        '--tag-very-low': 'tag-very-low'
    }
    Object.entries(mapping).forEach(([cssVar, key]) => {
        if (vars[key] != null) root.style.setProperty(cssVar, vars[key])
    })

    const isThemeB = process.env?.REACT_APP_SELECT_THEME === "themeb";

    let backgroundValue;

    if (isThemeB && vars["bg-main-image"]) {
        backgroundValue = `url(${vars["bg-main-image"]}) no-repeat center center fixed`;
    } else if (vars["bg-navbar"]) {
        backgroundValue = vars["bg-navbar"];
    }

    if (backgroundValue && document.body) {
        document.body.style.background = backgroundValue;
        document.body.style.backgroundSize = "cover"; // ✅ ต้องอยู่หลัง
    }
}

const updateManifestLinks = (cms) => {
    if (typeof document === 'undefined') return { url: null }
    const urlDomain = `${window.location.protocol}//${window.location.host}`
    const manifest = {
        short_name: cms['manifest_short_name'],
        name: cms['manifest_name'],
        icons: [
            { src: cms?.manifest_icon_xxl || '', type: 'image/webp', sizes: '512x512' },
            { src: cms?.manifest_icon_xxl_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '512x512' },
            { src: cms?.manifest_icon_xl || '', type: 'image/webp', sizes: '196x196' },
            { src: cms?.manifest_icon_xl_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '196x196' },
            { src: cms?.manifest_icon_lg || '', type: 'image/webp', sizes: '144x144' },
            { src: cms?.manifest_icon_lg_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '144x144' },
            { src: cms?.manifest_icon_md || '', type: 'image/webp', sizes: '128x128' },
            { src: cms?.manifest_icon_md_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '128x128' },
            { src: cms?.manifest_icon_sm || '', type: 'image/webp', sizes: '96x96' },
            { src: cms?.manifest_icon_sm_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '96x96' },
            { src: cms?.manifest_icon_xs || '', type: 'image/webp', sizes: '64x64' },
            { src: cms?.manifest_icon_xs_png || '', type: 'image/png, image/PNG, image/jpg, image/JPEG', sizes: '64x64' }
        ],
        scope: cms['manifest_scope'],
        start_url: urlDomain,
        display: cms['manifest_display'],
        theme_color: cms['manifest_theme_color'],
        background_color: cms['manifest_background_color']
    }
    const str = JSON.stringify(manifest)
    const blob = new Blob([str], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    const manifestEl = document.querySelector('#manifest-project')
    const appleEl = document.querySelector('#apple-touch-project')
    const iconRel = document.querySelector('#icon-rel')
    if (manifestEl) manifestEl.setAttribute('href', url)
    if (appleEl && cms['manifest_icon_lg']) appleEl.setAttribute('href', cms['manifest_icon_lg'])
    if (iconRel && cms['rel_icon_big']) iconRel.setAttribute('href', cms['rel_icon_big'])

    return { url }
}

const Layouts = ({ domain }) => {
    const dispatch = useDispatch()
    const dataSetting = useSelector((s) => s.reduxAppDataSetting.result)
    const reduxSpinner = useSelector((s) => s.reduxAppSpinner.data)
    const theme = useTheme()
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('sm'))

    const parsed = useParameterUrl()
    const { login, register, affiliate, refer, ref, market, token, clickid, fbclid, remember, fbcid, fbuuid, platform } = parsed

    const [dataSettingError, setDataSettingError] = useState(null)
    const [maintainnerStatus, setMaintainnerStatus] = useState(null)
    const [configInstallPWA, setConfigInstallPWA] = useState(null)
    const [notificationStatus, setNotificationStatus] = useState('load')
    const [processing, setProcessing] = useState(true)

    const gtmInitedRef = useRef(false)
    const manifestUrlRef = useRef(null)

    const isTokenMemo = useMemo(() => isToken(), [])
    const isLoggedInMemo = useMemo(() => isUserLoggedIn(), [])

    const renderRoutes = () => (
        isTokenMemo && (
            <Router>
                <ViewsRoutes />
                <ConfigSet dataCMS={dataSetting?.cms} />
            </Router>
        )
    )

    const setLoadDataCms = (dataCms) => {
        // Redux + cache
        dispatch(setDataCMS(dataCms))
        setStorage(STORAGE_KEYS.dataCMS, JSON.stringify(dataCms))

        // CSS vars + body background
        setCssVars(dataCms)

        // GTM (init once)
        if (dataCms['google_key'] && !gtmInitedRef.current) {
            TagManager.initialize({ gtmId: dataCms['google_key'] })
            gtmInitedRef.current = true
        }

        // Manifest + icons
        const { url } = updateManifestLinks(dataCms)
        if (manifestUrlRef.current) {
            // revoke URL เก่ากัน memory leak
            URL.revokeObjectURL(manifestUrlRef.current)
        }
        manifestUrlRef.current = url

        // PWA notification config
        if (dataCms?.section_notificaion_pwa === 1 || dataCms?.section_notificaion_pwa === '1') {
            const urlDomain = `${window.location.protocol}//${window.location.host}`
            setConfigInstallPWA({
                title: `แอพ ${dataCms['manifest_name']}`,
                body: `คลิกเพื่อติดตั้งแอพ ${dataCms['manifest_name']}`,
                text: dataCms['title_notificaion_pwa'],
                icon: dataCms['manifest_icon_lg'],
                data: { url: urlDomain }
            })
            const notif = getStorage(STORAGE_KEYS.notificationStatus)
            setNotificationStatus(notif)
        } else {
            setNotificationStatus('load')
        }
    }

    const renderContent = () => {
        if (!dataSettingError && !maintainnerStatus) return renderRoutes()
        if (maintainnerStatus) return <ErrorApp typeError='maintainer' />
        if (dataSettingError) return <ErrorApp typeError='noData' />
        return null
    }

    const reloadSetting = (setting) => {
        try {
            if (setting?.cms != null) {
                removeStorage(STORAGE_KEYS.dataCMS)
                setLoadDataCms(setting.cms)
            } else {
                const fromCache = getStorage(STORAGE_KEYS.dataCMS)
                if (fromCache) {
                    const cms = safeJSONParse(fromCache)
                    if (cms) setLoadDataCms(cms)
                    else setDataSettingError(true)
                }
            }

            const st = setting?.agent?.agent_staus // สะกดเดิมตามระบบ
            if (st === 2 || st === 0) setMaintainnerStatus(true)
            else setMaintainnerStatus(false)
        } catch {
            setDataSettingError(true)
        }
    }

    // 1) mark processing=false หากมี setting ใน cache เพื่อลด FCP
    useEffect(() => {
        const cached = getStorage(STORAGE_KEYS.setting)
        if (cached) {
            const obj = safeJSONParse(cached)
            if (obj) setProcessing(false)
        }
    }, [])

    // 2) เมื่อ processing=false ให้ reloadSetting จาก redux (dataSetting)
    useEffect(() => {
        if (!processing) reloadSetting(dataSetting)
    }, [processing, dataSetting])

    // 3) จัดการ query parameters (login/register/remember/affiliate/...)
    useEffect(() => {
        setStorage(STORAGE_KEYS.allusion, document.referrer)

        if (!isLoggedInMemo) {
            if (truthy(login)) dispatch(openAuthenApp({ tab: 'login', show: true }))
            if (truthy(register)) dispatch(openAuthenApp({ tab: 'register', show: true }))
            if (truthy(remember)) dispatch(openAuthenApp({ tab: 'remember', show: true }))
        }

        if (affiliate) setStorage(STORAGE_KEYS.affiliate, affiliate)
        if (clickid) setStorage(STORAGE_KEYS.clickid, clickid)
        if (platform) setStorage(STORAGE_KEYS.platform, platform)

        // Facebook params (fbclid/fbcid/fbuuid)
        const fbParam = [fbclid, fbcid, fbuuid].find(Boolean)
        if (fbParam) {
            const prev = getStorage(STORAGE_KEYS.fbclid)
            setStorage(STORAGE_KEYS.fbclid, fbParam)
            if (prev !== fbParam) {
                setStorage(STORAGE_KEYS.fbctimestamp, Date.now())
            }
        }

        // refer / ref
        if (refer) setStorage(STORAGE_KEYS.refer, refer)
        if (ref) setStorage(STORAGE_KEYS.refer, ref)

        if (market) setStorage(STORAGE_KEYS.market, market)
    }, [login, register, remember, affiliate, refer, ref, market, clickid, fbclid, fbcid, fbuuid, platform, dispatch, isLoggedInMemo])

    // 4) ดึง setting จาก API เมื่อมี token
    useEffectOnce(() => {
        if (isTokenMemo) {
            dispatch(loadSettingAction({ domain }, setDataSettingError, null, setProcessing))
        }
    })

    // 5) เมื่อ setting พร้อมแล้วค่อยโหลด camp
    useEffect(() => {
        if (dataSettingError === false) {
            dispatch(loadDataCampAll({ type: 99 }))
        }
    }, [dataSettingError, dispatch])

    // 6) live chat เมื่อ dataSetting เปลี่ยน
    useEffect(() => {
        const contacts = dataSetting?.agent_contact
        if (Array.isArray(contacts) && contacts.length) {
            const live = contacts.find((it) => it.contact_channel === 1)
            if (live?.channel_appid) loadLiveChat(live.channel_appid)
        }
    }, [dataSetting])

    // 7) auto login by token param หากยังไม่ login
    useEffectOnce(() => {
        if (!isLoggedInMemo && token) {
            dispatch(loginToken({ token }))
        }
    })

    // cleanup manifest object URL
    useEffect(() => () => {
        if (manifestUrlRef.current) URL.revokeObjectURL(manifestUrlRef.current)
    }, [])

    return (
        <>
            {reduxSpinner?.status && <LoadSpinner textProcess={reduxSpinner.textProcess} />}
            {renderContent()}
            {notificationStatus !== 'load' &&
                isLargeScreen &&
                (notificationStatus === null || notificationStatus === undefined || notificationStatus === '') &&
                configInstallPWA && (
                    <NotificationSubscription
                        setStorage={setStorage}
                        configInstallPWA={configInstallPWA}
                        notificationStatus={notificationStatus === null || notificationStatus === undefined || notificationStatus === ''}
                    />
                )}
        </>
    )
}

export default Layouts